<template>
  <div>
    <h3 class="greenTitle">Regularización de órdenes y notificacion TRMG</h3>

    <el-row :gutter="20">
      <el-col :sm="24" :md="8" :lg="6">
        <p class="fieldLabel">TIPO DE REGULARIZACIÓN</p>
        <el-radio-group v-model="action" :disabled="loadingPreview || sending" @change="resetPreview">
          <el-radio-button label="terminate">Terminado</el-radio-button>
          <el-radio-button label="pending">Pendiente</el-radio-button>
        </el-radio-group>
      </el-col>
      <el-col :sm="24" :md="16" :lg="18">
        <p class="fieldLabel">ÓRDENES</p>
        <el-radio-group v-model="searchType" :disabled="loadingPreview || sending" @change="resetPreview">
          <el-radio-button label="soc">SOC</el-radio-button>
          <el-radio-button label="lpn">LPN</el-radio-button>
        </el-radio-group>
        <el-input
          v-model="rawInput"
          type="textarea"
          :rows="7"
          resize="none"
          :disabled="loadingPreview || sending"
          :placeholder="inputPlaceholder"
          @input="resetPreview" />
      </el-col>
    </el-row>

    <el-row class="mt-1">
      <el-button
        class="default"
        icon="el-icon-search"
        :disabled="!hasInput || loadingPreview || sending"
        @click="previewOrders">
        PREVISUALIZAR
      </el-button>
      <el-button
        icon="el-icon-refresh"
        :disabled="loadingPreview || sending"
        @click="clearForm">
        LIMPIAR
      </el-button>
    </el-row>

    <el-alert
      v-if="invalidRows.length > 0"
      class="mt-1"
      type="warning"
      :closable="false"
      :title="invalidRowsMessage"
      show-icon />

    <el-row v-if="loadingPreview" class="mt-1">
      <p class="loadingText">
        <i class="el-icon-loading" />
        Consultando órdenes en Planning
      </p>
    </el-row>

    <el-table
      v-if="orders.length > 0"
      class="mt-1"
      :data="orders"
      border>
      <el-table-column label="RUTA" prop="routeID" width="100">
        <template #default="scope">
          {{ scope.row.routeID || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="SOC" prop="subOrderId" width="160" />
      <el-table-column label="LPN" prop="lpn" min-width="180">
        <template #default="scope">
          {{ scope.row.lpn || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="ESTADO ACTUAL" prop="currentStatus" min-width="150">
        <template #default="scope">
          {{ scope.row.currentStatus || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="VALIDACIÓN" width="140">
        <template #default="scope">
          <el-tag :type="scope.row.lookupType" size="mini">
            {{ scope.row.lookupLabel }}
          </el-tag>
        </template>
      </el-table-column>
    </el-table>

    <el-row v-if="orders.length > 0" class="mt-1">
      <el-button
        class="default right"
        icon="el-icon-check"
        :disabled="!canSend"
        @click="confirmRegularization">
        ENVIAR REGULARIZACIÓN
      </el-button>
    </el-row>

    <el-row v-if="sending" class="mt-1">
      <p class="loadingText">
        <i class="el-icon-loading" />
        Enviando regularización
      </p>
    </el-row>
  </div>
</template>

<script>
import { apiRequest } from '@/backend/vue-axios/axios';

export default {
  name: 'RegularizeOrders',
  data() {
    return {
      action: 'terminate',
      searchType: 'soc',
      rawInput: '',
      orders: [],
      invalidRows: [],
      loadingPreview: false,
      sending: false,
    };
  },
  computed: {
    inputPlaceholder() {
      return this.searchType === 'lpn'
        ? 'LPN separados por salto de linea, coma o espacio'
        : 'SOC separadas por salto de linea, coma o espacio';
    },
    hasInput() {
      return this.rawInput.trim() !== '';
    },
    canSend() {
      return !this.sending && !this.loadingPreview && this.orders.length > 0
        && this.orders.every(order => order.subOrderId);
    },
    invalidRowsMessage() {
      return `Se omitieron filas con formato invalido: ${this.invalidRows.join(', ')}`;
    },
    actionLabel() {
      return this.action === 'pending' ? 'Pendiente' : 'Terminado';
    },
    routeSummary() {
      const routes = this.orders
        .map(order => order.routeID)
        .filter(routeID => routeID)
        .filter((routeID, index, list) => list.indexOf(routeID) === index);
      const hasOrdersWithoutRoute = this.orders.some(order => !order.routeID);

      if (routes.length === 0 && hasOrdersWithoutRoute) {
        return 'No se encontro ruta para las ordenes consultadas.';
      }

      const summary = `Rutas encontradas: ${routes.join(', ')}.`;
      if (hasOrdersWithoutRoute) {
        return `${summary} Hay ordenes sin ruta explicita; Toolkit usara la ultima ruta registrada.`;
      }
      return summary;
    },
  },
  methods: {
    resetPreview() {
      this.orders = [];
      this.invalidRows = [];
    },
    clearForm() {
      this.rawInput = '';
      this.resetPreview();
    },
    parseRows() {
      const parsedRows = [];
      const invalidRows = [];
      const uniqueKeys = {};
      const lines = this.rawInput
        .split(/[\s,;]+/)
        .map(line => line.trim())
        .filter(line => line !== '');

      lines.forEach((line, index) => {
        const subOrderId = this.searchType === 'soc' ? Number(line) : null;
        const subOrderIsValid = this.searchType === 'lpn'
          || (Number.isSafeInteger(subOrderId) && subOrderId > 0);

        if (!subOrderIsValid) {
          invalidRows.push(index + 1);
          return;
        }

        const key = line;
        if (uniqueKeys[key]) return;

        uniqueKeys[key] = true;
        parsedRows.push({
          routeID: 0,
          subOrderId,
          lpn: this.searchType === 'lpn' ? line : '',
          currentStatus: '',
          lookupType: 'info',
          lookupLabel: 'Pendiente',
        });
      });

      this.invalidRows = invalidRows;
      return parsedRows;
    },
    previewOrders() {
      const parsedRows = this.parseRows();
      if (parsedRows.length === 0) {
        this.$notify({
          type: 'warning',
          title: 'Advertencia',
          message: 'Ingresa al menos una fila valida.',
        });
        return;
      }

      this.orders = parsedRows;
      this.loadingPreview = true;
      apiRequest({
        module: 'planning',
        service: 'search_state_orders',
        params: {
          search_type: this.searchType,
          ...(this.searchType === 'lpn'
            ? { lpns: parsedRows.map(row => row.lpn) }
            : { suborders: parsedRows.map(row => row.subOrderId) }),
        },
      }).then((response) => {
        const lookup = this.buildLookup(response.data.orders || []);
        this.orders = parsedRows.map(row => this.mapOrderWithLookup(row, lookup));
        this.loadingPreview = false;
      }).catch((error) => {
        this.loadingPreview = false;
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'No fue posible consultar las ordenes en Planning.';

        this.$notify({
          type: 'info',
          title: 'Información',
          message,
        });
      });
    },
    buildLookup(orders) {
      const lookup = {};
      orders.forEach((order) => {
        lookup[String(this.searchType === 'lpn' ? order.lpn : order.code)] = order;
      });
      return lookup;
    },
    mapOrderWithLookup(row, lookup) {
      const order = lookup[String(this.searchType === 'lpn' ? row.lpn : row.subOrderId)];
      if (!order) {
        return {
          ...row,
          lookupType: 'warning',
          lookupLabel: 'No encontrada',
        };
      }

      return {
        ...row,
        subOrderId: order.code,
        routeID: order.route_id || 0,
        lpn: order.lpn || '',
        currentStatus: order.status || '',
        lookupType: 'success',
        lookupLabel: 'Encontrada',
      };
    },
    confirmRegularization() {
      if (!this.canSend) return;
      const ordersWithoutRoute = this.orders.filter(order => !order.routeID);
      if (this.action === 'pending' && ordersWithoutRoute.length > 0) {
        this.$notify({
          type: 'warning',
          title: 'Advertencia',
          message: 'Todas las ordenes pendientes deben tener una ruta encontrada.',
        });
        return;
      }

      this.$confirm(
        `${this.routeSummary} Se enviaran ${this.orders.length} ordenes como ${this.actionLabel} a TRMG. ¿Quieres continuar?`,
        'Confirmar regularización',
        {
          confirmButtonText: 'Continuar',
          cancelButtonText: 'Cancelar',
          type: 'warning',
        },
      ).then(() => {
        this.sendRegularization();
      }).catch(() => {});
    },
    sendRegularization() {
      this.sending = true;
      apiRequest({
        module: 'toolkit',
        service: 'sendStateTrmg',
        params: this.buildPayload(),
      }).then(() => {
        this.sending = false;
        this.$notify({
          type: 'success',
          title: 'Información',
          message: 'Regularización enviada correctamente.',
        });
      }).catch((error) => {
        this.sending = false;
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'No fue posible enviar la regularización.';

        this.$notify({
          type: 'error',
          title: 'Error',
          message,
        });
      });
    },
    buildPayload() {
      const routesMap = {};
      this.orders.forEach((order) => {
        const routeID = order.routeID || 0;
        if (!routesMap[routeID]) {
          routesMap[routeID] = {
            routeID,
            subOrders: [],
          };
        }

        if (!routesMap[routeID].subOrders.includes(order.subOrderId)) {
          routesMap[routeID].subOrders.push(order.subOrderId);
        }
      });

      const routes = Object.keys(routesMap).map((routeID) => {
        const route = routesMap[routeID];
        return {
          routeID: route.routeID,
          subOrders: route.subOrders,
        };
      });

      const payload = { routes };
      if (this.action === 'pending') {
        payload.action = 'pending';
      }
      return payload;
    },
  },
};
</script>

<style scoped>
.fieldLabel {
  color: #606266;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 8px;
}

.mt-1 {
  margin-top: 1rem;
}

.right {
  float: right;
}

.loadingText {
  color: #A0BE03;
  font-size: 14px;
}

.loadingText i {
  margin-right: 5px;
}
</style>
